/* eslint-disable max-lines */

/** Common props used to receive click-tracking info */
export type TrackingDataProps<T = object> = T & {
  readonly trackingData?: LinkTrackingData;
  readonly trackingId?: string;
};

/** The set of properties which can be attached to a link for tracking */
export interface LinkTrackingData {
  accordionAction?: TrackingDataAccordionAction;
  accordionTitle?: TrackingDataAccordionTitle | TrackingDataAccordionTitleFluVaccine | string;
  apptTime?: string;
  consumerOfferName?: string;
  csArea?: UnverifiedCsArea;
  csPosition?: number;
  cta?: TrackingDataCta;
  displayAdType?: 'external';
  event?: TrackingDataEvent;
  location?: TrackingDataLocation;
  loginType?: TrackingDataLoginType;
  partnerName?: string;
  practiceName?: string;
  practiceId?: string;
  practitionerId?: string;
  practitionerName?: string;
  practiceInterests?: string[];
  practicePreferredProviders?: string[];
  profileNavLink?: string;
  searchCardType?: SearchCardType;
  searchCardArea?: SearchCardArea;
  searchCardBulkBilling?: boolean;
  searchCardFeatured?: boolean;
  searchCardHasAppointments?: boolean;
  searchCardResultsPosition?: number;
  searchCardPriceBracket?: 'low' | 'med' | 'high';
  searchCardPriceVerified?: boolean;
  searchCardSpecialOffer?: boolean;
  searchResultType?: 'extended' | 'standard';
  searchResultIsInSuburb?: boolean;
  searchSuggestionType?: SearchSuggestionType;
  searchSuggestionTab?: SearchSuggestionTabType;
  specialOfferAction?: SpecialOfferAction;
  specialOfferName?: string;
  specialty?: string;
  trackedelementId?: TrackedElementId;
  trackedelementAction?: TrackedElementAction;
  trackedelementValue?: string;
  unverifiedCsType?: UnverifiedCsType;
}

export enum TrackedElementId {
  // Step Header
  BACK_FROM_HEADER = 'backFromHeader',
  IS_THIS_EMERGENCY = 'isThisEmergency',

  // Overview Step
  ABOUT_BOOK_THIS_APPT = 'bookThisAppt',

  // Medical certificate overview Step
  MEDICAL_CERTIFICATE_OVERVIEW = 'medicalCertificateOverviewContinue',
  MEDICAL_CERTIFICATE_MEDICAL_QUESTIONS = 'medicalCertificateMedicalQuestionsContinue',
  MEDICAL_CERTIFICATE_ELIGIBILITY_QUESTIONS = 'medicalCertificateIllegibilityContinue',

  // Medical certificate medical questions
  MEDICAL_CERTIFICATE_UNWELL = 'unwellQuestion',
  MEDICAL_CERTIFICATE_URGENT = 'urgentQuestion',
  MEDICAL_CERTIFICATE_CARING_FOR = 'caringForQuestion',
  MEDICAL_CERTIFICATE_SYMPTOMS = 'symptomsQuestion',
  MEDICAL_CERTIFICATE_CONSULT_GP = 'consultGPQuestion',
  MEDICAL_CERTIFICATE_UNDERSTOOD_ALL_QUESTION = 'understoodAllQuestions',

  // Specialty Step
  APPT_SPECIALTY_BUTTON = 'specialtyButton',

  // Appointment Type Step
  APPT_TYPE_BOOK_FOR_MYSELF = 'bookForMyself',
  APPT_TYPE_BOOK_FOR_OTHER = 'bookForOther',
  APPT_TYPE_BOOK_TELEHEALTH = 'apptTypeBookTelehealth',
  APPT_TYPE_BOOKING_TYPE_BUTTON = 'bookingTypeButton',
  APPT_TYPE_CONTACT_BOTH = 'contactBoth',
  APPT_TYPE_CONTACT_ME = 'contactMe',
  APPT_TYPE_CONTACT_PATIENT = 'contactPatient',
  APPT_TYPE_NEW_PATIENT_SELECT = 'newPatientSelect',
  APPT_TYPE_RETURN_PATIENT_SELECT = 'returnPatientSelect',

  // Policy Step
  POLICY_CONTINUE_BUTTON = 'bookEmbeddedPolicyContinue',

  // Personal Details Step
  DETAILS_BOOKER_EMAIL = 'bookerEmail',
  DETAILS_BOOKER_FIRST_NAME = 'bookerFirstName',
  DETAILS_BOOKER_LAST_NAME = 'bookerLastName',
  DETAILS_BOOKER_PHONE = 'bookerPhone',
  DETAILS_CONTINUE_BUTTON = 'detailsContinueBtn',
  DETAILS_PATIENT_ADDRESS = 'patientAddress',
  DETAILS_PATIENT_BIRTHDAY = 'patientBirthday',
  DETAILS_PATIENT_EMAIL = 'patientEmail',
  DETAILS_PATIENT_FIRST_NAME = 'patientFirstName',
  DETAILS_PATIENT_LAST_NAME = 'patientLastName',
  DETAILS_PATIENT_MEDICARE = 'patientMedicare',
  DETAILS_PATIENT_MOBILE = 'patientMobile',
  DETAILS_PATIENT_SUBURB = 'patientSuburb',
  DETAILS_TERMS_COLLECTION = 'heCollection',
  DETAILS_TERMS_PRIVACY = 'hePrivacy',
  DETAILS_TERMS_TERMS = 'heTerms',

  // Confirm Duplicate Step
  CANCEL_DUPLICATE_BOOKING = 'cancelDuplicateBooking',
  CONFIRM_DUPLICATE_BOOKING = 'confirmDuplicateBooking',

  // Verification Step
  RESEND_VERIFICAION_LINK = 'resendVerificationLink',
  CHANGE_MOBILE_LINK = 'changeMobileLink',
  NEW_PHONE_INPUT = 'newPhoneInput',
  NEW_PHONE_SEND_VERIFICATION = 'newPhoneSendVerification',

  // Med Delivery Opt In
  MED_DELIVERY_OPT_IN = 'medDeliveryOptIn',

  // Referral Upload Step
  REFERRAL_UPLOAD_CONTINUE = 'ctcContinueBtn',

  // Privacy preference
  SUBSCRIBE_PRIVACY_PREFERENCE = 'subscribePrivacyPreference',
}

export enum TrackedElementAction {
  CLICK = 'click',
  FOCUS = 'focus',
  CHECK = 'check',
}

export enum SearchSuggestionType {
  LOCATION = 'Location',
  PRACTICE = 'Practice',
  PRACTITIONER = 'Practitioner',
  SPECIALTY = 'Specialty',
  TOPIC = 'Topic',
  TEXT_SEARCH = 'Text Search',
}

export enum SearchSuggestionTabType {
  ALL = 'All',
  SPECIALTY = 'Specialty',
  PRACTICE = 'Practice',
  PRACTITIONER = 'Practitioner',
  Topics = 'Topics',
}

export enum TrackingDataAccordionAction {
  CLOSE = 'close',
  OPEN = 'open',
}

export enum TrackingDataAccordionTitleFluVaccine {
  SHOULD_I_GET_THE_FLU_VACCINE = 'Should I get the flu vaccine?',
  WHO_SHOULD_GET_THE_FLU_VACCINE = 'Who should get the flu vaccine?',
  WHEN_SHOULD_GET_THE_FLU_VACCINE = 'When should I get the flu vaccine?',
  AM_I_ELIGIBLE_FOR_FREE_FLU_VACCINE = 'Am I eligible for a free flu vaccine?',
  IS_THE_FLU_VACCINE_SAFE = 'Is the flu vaccine safe?',
  WHAT_IF_IVE_RECENTLY_HAD_A_COVID_VACCINATION = 'What if I’ve recently had or am soon having a COVID-19 vaccination?',
  DO_I_NEED_TO_GET_VACCINATED_AGAIN_IF_LAST_YEAR = 'Do I need to get a flu vaccination again if I was vaccinated last year?',
  CAN_I_GET_VACCINATED_IF_I_HAVE_EGG_ALLERGY = 'Can I get vaccinated if I have an egg allergy?',
  WHAT_IS_THE_AUSTRALIAN_IMMUNISATION_REGISTER = 'What is the Australian Immunisation Register?',
  SOMETIMES_PEOPLE_CAN_GET_INFLUENZE_EVEN_WHEN_VACCINATED_WHY_HAPPEN = 'Sometimes people can get influenza even when they have been vaccinated —why does this happen?',
  WHICH_FLU_VACCINES_ARE_APPROVED_FOR_USE_IN_AUSTRALIA = 'Which flu vaccines are approved for use in Australia?',
  CAN_I_GET_THE_FLU_VACCINE_IF_IVE_JUST_HAD_THE_COVID_19_SHOT = 'Can I get the flu vaccine if I’ve just had the COVID-19 shot?',
  HOW_MUCH_DO_FLU_VACCINES_COST = 'How much do flu vaccines cost?',
  WHERE_CAN_I_GET_THE_FLU_VACCINE_IN_AUSTRALIA = 'Where can I get the flu vaccine in Australia?',
}

export enum TrackingDataAccordionTitle {
  CAN_I_STILL_GET_PRESCRIPTIONS_FOR_MEDICATION_OVER_TELEHEALTH = 'Can I still get prescriptions for medication over Telehealth?',
  HOW_DO_I_PAY_FOR_A_PRIVATE_TELEHEALTH_CONSULT = 'How do I pay for a private telehealth consult?',
  HOW_MUCH_DOES_A_TELEHEALTH_APPOINTMENT_COST = 'How much does a telehealth appointment cost?',
  WHAT_CAN_BE_DONE_VIA_TELEHEALTH = 'What can be done via telehealth?',
  WHAT_DO_I_DO_IF_I_NEED_TO_CANCEL_AND_GET_A_REFUND = 'What do I do if I need to cancel and get a refund?',
  WHAT_EQUIPMENT_DO_I_NEED_TO_DO_TELEHEALTH = 'What equipment do I need to do telehealth?',
  WHAT_IS_TELEHEALTH = 'What is telehealth?',
}

export enum TrackingDataCta {
  FIND_24_7_TELEHEALTH_PRACTICES = 'Find 24/7 telehealth practices',
  FIND_TELEHEALTH_ENABLED_PRACTICES = 'Find telehealth-enabled practices',
  PATIENT_DETAILS_LOGIN = 'patient-details-login',
  PATIENT_DETAILS_SIGNUP = 'patient-details-signup',
  PATIENT_DETAILS_CONTINUE_AS_GUEST = 'patient-details-continue-as-guest',
  PREFERRED_LANGUAGE_AGREE = 'Preferred Language - I agree',
  PREFERRED_LANGUAGE_CANCEL = 'Preferred Language - Cancel',
  SEE_FUNDS = 'See funds',
  SEE_TELEHEALTH_PROVIDERS = 'See Telehealth providers',
  GET_MEDICAL_CERTIFICATE = 'Get a medical certificate',
  GET_PRESCRIPTION = 'Get a prescription',
}

export enum TrackingDataEvent {
  ACCORDION = 'accordion',
  ADD_TO_MY_CARE_TEAM = 'Add to My Care Team',
  APPOINTMENT_TIME = 'Appointment time',
  APPOINTMENT_PROVIDER = 'Appointments selected provider',
  BOOK_BUTTON = 'Book button',
  CONSUMER_OFFER_REDEEM = 'consumer-offer-redeem',
  CTA_PRIMARY = 'cta-primary',
  CTA_SECONDARY = 'cta-secondary',
  DISPLAY_AD = 'display-ad',
  FAX_REVEAL = 'Fax reveal',
  FINAL_BOOK_APPT_BUTTON = 'bf-final-bookApptButton',
  GALLERY_INTERACTION = 'Gallery interaction',
  LIST_PRACTICE_AD = 'Provider Services Ad',
  MORE_TIME_BUTTON = 'More times button',
  PATIENT_LOGIN = 'patient-login',
  PATIENT_LOGIN_SIGN_UP = 'patient-login-sign-up',
  PHONE_REVEAL = 'Phone reveal',
  PRACTICE_FAX_CLICK = 'Practice fax click',
  PRACTICE_MAP = 'Practice map',
  PRACTICE_PHONE_CLICK = 'Practice telephone click',
  PRACTICE_WEBSITE_LINK = 'Practice website link',
  PRIVACY_SUMMARY = 'privacy-summary',
  PROFILE_BOOKING_BOX_SPECIALTY_DROPDOWN = 'Profile booking box specialty dropdown',
  PROFILE_APPOINTMENT_TYPE_FIRST_BOX_APPT_TYPE_DROPDOWN = 'Profile booking box appointment type dropdown',
  PROFILE_APPOINTMENT_TYPE_FIRST_BOX_PATIENT_TYPE_DROPDOWN = 'Profile booking box newExisting selection',
  PROFILE_BOOKING_BOX_PRACTITIONER_DROPDOWN = 'Profile booking box practitioner dropdown',
  PROFILE_BUTTON = 'Profile button',
  PROFILE_FACILITIES_SHOW_MORE = 'Profile facilities show more',
  PROFILE_FACILITIES_SHOW_LESS = 'Profile facilities show less',
  PROFILE_NAV_ITEM = 'Profile nav item',
  PROFILE_OVERVIEW_SHOW_MORE = 'Profile overview show more',
  PROFILE_OVERVIEW_SHOW_LESS = 'Profile overview show less',
  PROFILE_PRICING_BUNDLE_SEE_DETAILS = 'Profile pricing bundle see details',
  PROFILE_PRICING_BUNDLE_HIDE_DETAILS = 'Profile pricing bundle hide details',
  PROFILE_PRICING_TREATMENT_SEE_DETAILS = 'Profile pricing treatment see details',
  PROFILE_PRICING_TREATMENT_HIDE_DETAILS = 'Profile pricing treatment hide details',
  PROFILE_SPECIAL_INTERESTS_SHOW_MORE = 'Profile special interests show more',
  PROFILE_SPECIAL_INTERESTS_SHOW_LESS = 'Profile special interests show less',
  REMOVE_FROM_MY_CARE_TEAM = 'Remove from My Care Team',
  SEARCH_BOX_SELECTOR_OPTION_CLICK = 'Search Box Selector Option Click',
  SEARCH_BAR_SEARCH_ICON = 'Search Bar Search Icon',
  SEARCH_RESULT_CARD = 'search result card',
  SPECIAL_OFFER = 'special-offer',
  UNVERIFIED_PROFILE_CS = 'Unverified profile cs',
  UNVERIFIED_CLAIM_YOUR_PROFILE = 'Unverified claim your profile',
  UNVERIFIED_FIND_OUT_MORE = 'Unverified find out more',
}

export enum TrackingDataLocation {
  PATIENT_DETAILS = 'patient details',
  PROFILE_BOOK_SPECIALTIES_SECTION = 'Profile book specialties section',
  PROFILE_BOOKING_BOX = 'Profile booking box',
  PROFILE_PEOPLE_CARD = 'Profile people card',
  PRACTICE_INFO_CONTACT_DETAILS = 'Practice Info Contact Details',
  PRACTICE_INFO_PHONE_REVEAL = 'Practice info phone reveal',
  PRACTICE_INFO_FAX_REVEAL = 'Practice info fax reveal',
  LANDING_PAGE_BOOKING_BOX = 'Landing page booking box',
  TELEHEALTH_SCHEDULED_APPOINTMENT = 'Telehealth Scheduled Appointment',
  HOMEPAGE_HERO = 'Homepage Hero',
  PAGE_CONTENT = 'Page content',
  BANNER = 'Banner',
  FOOTER = 'Footer',
}

export enum TrackingDataLoginType {
  GOOGLE = 'google',
  FACEBOOK = 'facebook',
  APPLE = 'apple',
  EMAIL = 'email',
}

export enum SearchCardArea {
  INFO = 'info',
  NEXT_AVAILABLE = 'next available appt',
  MORE_APPOINTMENTS = 'plus appts button',
  ALL_AVAILABILITIES = 'all availabilities button',
  REQUEST_APPOINTMENT = 'request appt button',
  CALL_PRACTICE_BUTTON = 'call practice button',
  DOWNLOAD_APP_BUTTON = 'Download app button',
  PRACTITIONER_BUBBLE = 'practitioner bubble',
}

export enum SearchCardType {
  PRACTICE = 'practice',
  PRACTITIONER = 'practitioner',
}

export enum UnverifiedCsType {
  NEARBY = 'nearby',
}

export enum UnverifiedCsArea {
  // Unverified Profiles Nearby appointments cs-area values
  PRACTICE_NAME = 'practice name',
  APPOINTMENT_TIME = 'appointment time',
  MORE_TIMES = 'more times',
  VIEW_MORE = 'view more',
}

export enum UnverifiedCsIcidQueryString {
  // These are opaque constants used for analytics in the form of query strings
  // e.g. ?icid=unp:pro:unp:prp:paa::prn:::AO they have no code meaning~

  ICID_PRACTICE_NAME = 'unp:pro:unp:prp:paa::prn:::AO',
  ICID_APPOINTMENT_TIME = 'unp:pro:unp:prp:paa::apt:::AO',
  ICID_MORE_TIMES = 'unp:pro:unp:prp:paa::mtb:::AO',
  ICID_VIEW_MORE = 'unp:pro:unp:prp:paa::vmn:::AO',
  ICID_GP_FIND_COVAX = 'cov:serp:alb:::gp:fcw:::mar21',
  ICID_GP_FIND_FLUVAX = 'icid=flv:serp:alb::::fcw:::may21',
}

export enum SpecialOfferAction {
  VIEW_TERMS_CONDITIONS = 'View terms & conditions',
  BOOK = 'Book',
}

export enum PractitionerIcidQueryString {
  ICID_PRACTICE_NAME = 'unp:pro:unp:stp:paa::prn:::AO',
  ICID_APPOINTMENT_TIME = 'unp:pro:unp:stp:paa::apt:::AO',
  ICID_MORE_TIMES = 'unp:pro:unp:stp:paa::mtb:::AO',
  ICID_VIEW_MORE = 'unp:pro:unp:stp:paa::vmn:::AO',
}
